import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	Inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ContactGroup } from '@campaign-portal/namespace/entities/contact-groups/specs';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { CountryPhoneCode } from '@campaign-portal/namespace/entities/countries/specs';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { FilesReports } from '@campaign-portal/namespace/entities/files/specs';

import {
	ALARIS_PROPERTY_MATCHER,
	AlarisDialogService,
	AlarisFileLoaderComponent,
	AlarisFilesService,
	AlarisFilterPipe,
	AlarisLinkComponent,
	AlarisLoadingDirective,
	AlarisMultiOptions2Component,
	AlarisMultiSelect2Component,
	AlarisMultiSelectFilter2Component,
	AlarisOptions2Component,
	AlarisProfileService,
	AlarisSelect2Component,
	AlarisSelectFilter2Component,
	AlarisTabsButtonComponent,
	AlarisTextAreaComponent,
	ChartUtilsService,
	DialogData,
	DialogNotificationType,
	filterWildcardFn,
	Matcher,
	PROFILE_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';

import { CountriesService } from '@helpers/services/countries.service';
import { sumElementsInArray } from '@helpers/utils/math';
import { CP_PERMISSIONS } from '@helpers/types/permissions';
import { CampaignWizardService, CONTACTS_SPLIT_PATTERN } from '../campaign-wizard.service';
import { ContactGroupsService } from '../../../contacts/services/contact-groups.service';
import { phone } from '../../campaign-from-file/fields';
import { CampaignStatisticsChartComponent } from '../campaign-statistics-chart/campaign-statistics-chart.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum CampaignRecipientsType {
	GROUP,
	MANUALLY,
	FILE,
}

const ChangeRecipientsDialogData: DialogData<boolean> = {
	title: 'campaigns.recipientsType.changeType',
	note: 'campaigns.recipientsType.changeTypeNote',
	closeAction: () => false,
	confirmAction: () => true,
	confirmButton: 'actions.confirm',
	closeButton: 'actions.close',
	result: true,
	type: DialogNotificationType.WARNING,
};

@Component({
	selector: 'app-campaign-recipients',
	templateUrl: './campaign-recipients.component.html',
	styleUrls: ['./campaign-recipients.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslateModule,
		AlarisLoadingDirective,
		AlarisTabsButtonComponent,
		AlarisMultiSelect2Component,
		AlarisFileLoaderComponent,
		AlarisMultiOptions2Component,
		AlarisSelectFilter2Component,
		AlarisSelect2Component,
		ReactiveFormsModule,
		AlarisOptions2Component,
		AlarisTextAreaComponent,
		AlarisLinkComponent,
		CampaignStatisticsChartComponent,
		AsyncPipe,
		AlarisMultiSelectFilter2Component,
		AlarisFilterPipe,
	],
	standalone: true,
})
export class CampaignRecipientsComponent implements OnInit, OnDestroy {
	readonly CampaignRecipientsType = CampaignRecipientsType;
	readonly tabs = [
		{
			label: 'campaigns.recipientsType.manually',
			value: CampaignRecipientsType.MANUALLY,
		},
		{
			label: 'campaigns.recipientsType.file',
			value: CampaignRecipientsType.FILE,
		},
	];
	readonly recipientsType = new FormControl<CampaignRecipientsType>(CampaignRecipientsType.MANUALLY, {
		nonNullable: true,
	});
	_recipientsType = CampaignRecipientsType.MANUALLY;

	// Charts
	contactGroupValue: number[] = [];
	contactGroupLabels: string[] = [];
	manualPhonesValue: number[] = [];
	fileUploadAnalysis: number[] = [];

	readonly errors = [
		{ key: 'pattern', value: 'errors.pattern' },
		{ key: 'limitExceeded', value: 'errors.limitExceeded' },
		{ key: 'required', value: 'errors.required' },
	];

	readonly groupsFilter = new FormControl<string | null>('');
	readonly countriesFilter = new FormControl<string | null>('');
	readonly groupsMatcher = ALARIS_PROPERTY_MATCHER<ContactGroup>('name');
	readonly fileLoading$ = new BehaviorSubject(false);

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(PROFILE_SERVICE_INJECTOR) private readonly profile: AlarisProfileService,
		public readonly contactGroupsService: ContactGroupsService,
		public readonly countriesService: CountriesService,
		public readonly cw: CampaignWizardService,
		private readonly filesService: AlarisFilesService,
		private readonly dialog: AlarisDialogService,
		private readonly cd: ChangeDetectorRef,
		private readonly translate: TranslateService,
		private readonly chartUtils: ChartUtilsService
	) {}

	readonly countryCodeMatcher: Matcher<CountryPhoneCode> = (country, search: string | null) => {
		const entry = (country.code ? `(+${country.code}) ` : '') + country.name;
		return filterWildcardFn(entry, search ?? '');
	};

	ngOnInit(): void {
		this.initializeTabs();
		this.initializeRecipientsType();
		this.subscribeToFormChanges();
	}

	ngOnDestroy(): void {
		this.fileLoading$.complete();
	}

	addFiles(files: (FileInfo | undefined)[]): void {
		const file = files[0];
		this.cw.contactsFile.patchValue(file);
		if (file) {
			this.prepareCharts(CampaignRecipientsType.FILE);
		} else {
			const resetData: FilesReports = { correctRow: 0, wrongFormatRow: 0, duplicatesRow: 0 };
			this.updateUploadFromFileChart(resetData);
		}
	}

	displayFnGroups(value: Id<exist>[] | null): string {
		return value === null
			? this.translate.instant('gl.all')
			: this.contactGroupsService.list
					.filter((group) => value.includes(group.id))
					.map((group) => this.translate.instant(group.name))
					.join(', ');
	}

	displayCountryCode(value: number): string {
		const selected = this.countriesService.map.get(value);
		return selected ? `( + ${selected.code} ) ${selected.name}` : '';
	}

	analyseFile(): void {
		const fileId = this.cw.contactsFile.value?.id;
		if (!fileId) {
			return;
		}
		this.fileLoading$.next(true);

		this.filesService
			.analysis({
				Data: {
					fileId,
					columnName: phone.variable,
					format: 'number',
					columns: [
						{
							columnName: phone.variable,
							order: 'col004',
						},
					],
				},
			})
			.pipe(takeUntil(this.cw.contactsFile.valueChanges), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (resp) => this.updateUploadFromFileChart(resp.Data),
				complete: () => this.fileLoading$.next(false),
			});
	}

	generateTemplate(): void {
		const csvContent = ['\ufeff', this.translate.instant('columns.phone'), '1111'].join('\n');
		this.downloadCSV(csvContent, 'contacts_template.csv');
	}

	private downloadCSV(content: string, filename: string): void {
		const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
		const url = window.URL.createObjectURL(blob);
		const anchor = this.document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		anchor.click();
		window.URL.revokeObjectURL(url);
	}

	private initializeRecipientsType(): void {
		this._recipientsType = this.cw.determineRecipientType();
		this.recipientsType.patchValue(this._recipientsType);
		this.enableControls(this._recipientsType);
	}

	private subscribeToFormChanges(): void {
		this.recipientsType.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((type) => this.changeRecipientsType(type));

		this.cw.contactsPlain.controls.text.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.prepareCharts(CampaignRecipientsType.MANUALLY));

		this.cw.contactGroups.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.prepareCharts(CampaignRecipientsType.GROUP));
	}

	private initializeTabs(): void {
		if (this.profile.allowed([CP_PERMISSIONS.CONTACTS_R])) {
			this.contactGroupsService.refresh$.next();
			this.tabs.unshift({ label: 'campaigns.recipientsType.contactGroups', value: CampaignRecipientsType.GROUP });
		}
	}

	private updateUploadFromFileChart({ correctRow, wrongFormatRow, duplicatesRow }: FilesReports): void {
		this.fileUploadAnalysis = [correctRow, wrongFormatRow, duplicatesRow];
		this.cw.totalRecipients.set(correctRow);
		this.cd.markForCheck();
	}

	private changeRecipientsType($event: CampaignRecipientsType): void {
		const { plain, file, groups } = this.cw.contactsControls.value;

		if (plain?.text || plain?.code || file || groups === null || (groups && groups.length > 0)) {
			this.dialog
				.openStock(ChangeRecipientsDialogData, DialogNotificationType.WARNING)
				.closed.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((result) => {
					if (result) {
						this.cw.contactsControls.reset({
							plain: { code: null, text: '' },
							groups: undefined,
							file: undefined,
						});
						this.enableControls($event);
						this._recipientsType = $event;
						this.cd.markForCheck();
					} else {
						this.recipientsType.patchValue(this._recipientsType, { emitEvent: false });
						this.cd.markForCheck();
					}
				});
		} else {
			this._recipientsType = $event;
			this.enableControls($event);
			this.cd.markForCheck();
		}
	}

	private enableControls(recipientsType: CampaignRecipientsType): void {
		this.cw.enableContactsControls(recipientsType);
		this.prepareCharts(recipientsType);
	}

	private prepareCharts(type: CampaignRecipientsType): void {
		switch (type) {
			case CampaignRecipientsType.GROUP:
				this.updateGroupChart();
				break;
			case CampaignRecipientsType.MANUALLY:
				this.updateManualChart();
				break;
			case CampaignRecipientsType.FILE:
				this.analyseFile();
				break;
		}
	}

	private updateGroupChart(): void {
		const value = this.cw.contactGroups.value;
		if (Array.isArray(value) && value.length === 1) {
			this.contactGroupValue = [this.contactGroupsService.map.get(value[0])!.contactsIncluded ?? 0];
			this.contactGroupLabels = [this.contactGroupsService.map.get(value[0])!.name];
			this.cw.totalRecipients.set(this.contactGroupsService.map.get(value[0])!.contactsIncluded ?? 0);
		} else if (value === null || (Array.isArray(value) && value.length > 1)) {
			this.contactGroupsService
				.analysis(value)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((resp) => {
					[this.contactGroupValue, this.contactGroupLabels] = this.chartUtils.sortData(
						...Object.entries(resp.Data.uniq).reduce(
							(result: [number[], string[]], item) => {
								result[0].push(item[1]);
								result[1].push(this.contactGroupsService.map.get(+item[0])?.name || '');
								return result;
							},
							[[], []]
						)
					);
					this.cw.totalRecipients.set(sumElementsInArray(this.contactGroupValue));
					this.contactGroupValue.push(resp.Data.duplicates);
					this.contactGroupLabels.push(this.translate.instant('campaigns.duplicates'));
				});
		} else {
			[this.contactGroupValue, this.contactGroupLabels] = [[], []];
			this.cw.totalRecipients.set(0);
		}
	}

	private updateManualChart(): void {
		const value = this.cw.contactsPlain.controls.text.value ?? '';
		const allPhones = value.split(CONTACTS_SPLIT_PATTERN).filter(Boolean);
		const uniquePhonesCount = new Set(allPhones).size;
		const duplicatesCount = allPhones.length - uniquePhonesCount;

		this.manualPhonesValue = [allPhones.length, duplicatesCount];
		this.cw.totalRecipients.set(uniquePhonesCount);
	}
}
